import React from 'react';
import {
  assetConfigurations,
  Flex,
  FormGroup,
  Grid,
} from '@mss/mss-component-library';
import Select from 'formik-antd/lib/select';
import Input from 'formik-antd/lib/input';
import { ErrorMessage, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { filterOption } from 'jsx/common/util/filterOption';
import CopyButton from '../Import/CopyButton';
import Button from '../../Button';

type CredentialsProps = {
  testConnection?: any;
  compact?: boolean;
  formIndex: number;
  sameConnection?: boolean;
  saveCredentials?: React.ReactNode;
  disabled?: boolean;
  copyToOtherAssets?: any;
};
type Option = {
  key: string;
  value: string | number;
  label: string;
  default?: boolean;
};

const versions: Option[] = [
  { key: 'v1', value: 1, label: 'v1' },
  { key: 'v2', value: 2, label: 'v2c' },
  { key: 'v3', value: 3, label: 'v3' },
];

const securityLevels: Option[] = [
  { key: 'sl1', value: 'no_auth_or_privacy', label: 'No auth or privacy' },
  { key: 'sl2', value: 'auth_without_privacy', label: 'Auth without privacy' },
  { key: 'sl3', value: 'auth_with_privacy', label: 'Auth with privacy' },
];
const authProtocols: Option[] = [
  { key: 'ap1', value: 'MD5', label: 'MD5' },
  { key: 'ap2', value: 'SHA', label: 'SHA' },
];
const privacyProtocols: Option[] = [
  { key: 'pp1', value: 'AES', label: 'AES' },
  { key: 'pp2', value: 'DES', label: 'DES' },
];

export const SNMPCredentialsForm = ({
  testConnection,
  formIndex,
  compact,
  sameConnection,
  saveCredentials,
  disabled,
}: CredentialsProps) => {
  const emptyNetworkAsset =
    assetConfigurations.network.assets.switch!.form.initialValues;
  const { values, handleChange, handleBlur, setFieldValue } =
    useFormikContext<any>();
  const [t] = useTranslation(['assetNetwork', 'asset', 'common']);
  const version = values.assets[formIndex].values.version ?? 1;
  const authColWidth =
    values.assets[formIndex].values.security_level === 'auth_with_privacy'
      ? 6
      : 12;

  const isV1orV2 = [1, 2].includes(version);
  const isV3 = version === 3;
  const isV3WithPrivacy =
    isV3 &&
    ['auth_with_privacy'].includes(
      values.assets[formIndex].values.security_level
    );
  const isV3WithAuth =
    3 === version &&
    ['auth_without_privacy', 'auth_with_privacy'].includes(
      values.assets[formIndex].values.security_level
    );
  const isV3NoAuthNoPrivacy =
    3 === version &&
    ['no_auth_or_privacy'].includes(
      values.assets[formIndex].values.security_level
    );

  const v3AndNoSecLevel =
    isV3 && !values.assets[formIndex].values.security_level;

  const noPrivacy =
    isV3WithPrivacy && !values.assets[formIndex].values.privacy_password;

  const noUserPass =
    isV3WithAuth &&
    (!values.assets[formIndex].values.security_username ||
      !values.assets[formIndex].values.auth_password);

  const noUser =
    isV3NoAuthNoPrivacy && !values.assets[formIndex].values.security_username;
  const noCommunity = isV1orV2 && !values.assets[formIndex].values.community;

  const createActions = (fieldName: string, isCustomField?: boolean) => (
    <CopyButton
      fieldName={fieldName}
      isCustomField={isCustomField}
      index={formIndex}
      subType={{
        key: 'network_type',
        value: values.assets[formIndex].values.network_type,
      }}
    />
  );
  return (
    <Grid.Row>
      <Grid.Col width={compact ? 4 : 12}>
        <Grid.Row>
          <Grid.Col>
            <FormGroup
              label={t('Version')}
              required={true}
              actions={createActions('version')}
              error={
                <ErrorMessage name={`assets.${formIndex}.values.version`} />
              }
              input={
                <Select
                  disabled={disabled}
                  name={`assets.${formIndex}.values.version`}
                  placeholder={`${t('Please select SNMP version')}...`}
                  optionFilterProp={'children'}
                  filterOption={filterOption}
                  value={version || undefined}
                  style={{ width: '100%' }}
                  onChange={newVersion => {
                    const resetTo3 =
                      newVersion === 3 && [1, 2].includes(version);
                    const resetTo1or2 =
                      [1, 2].includes(newVersion) && version === 3;
                    if (resetTo1or2) {
                      setFieldValue(`assets.${formIndex}.values`, {
                        ...values.assets[formIndex].values,
                        ...(resetTo1or2
                          ? {
                              security_level: emptyNetworkAsset.security_level,
                              security_username:
                                emptyNetworkAsset.security_username,
                              auth_protocol: emptyNetworkAsset.auth_protocol,
                              auth_password: emptyNetworkAsset.auth_password,
                              privacy_protocol:
                                emptyNetworkAsset.privacy_protocol,
                              privacy_password:
                                emptyNetworkAsset.privacy_password,
                            }
                          : {}),
                        ...(resetTo3
                          ? {
                              community: emptyNetworkAsset.community,
                            }
                          : {}),
                        version: newVersion,
                      });
                    }
                  }}
                  onBlur={handleBlur}
                  data-ta="assetVersionInput"
                >
                  {versions.map((option: Option) => (
                    <Select.Option key={option.key} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              }
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          {/* Version 3 - Show auth fields */}
          {isV3 && (
            <Grid.Col>
              <FormGroup
                label={t('Security Level')}
                actions={createActions('security_level')}
                required={true}
                error={
                  <ErrorMessage
                    name={`assets.${formIndex}.values.security_level`}
                  />
                }
                input={
                  <Select
                    disabled={disabled}
                    name={`assets.${formIndex}.values.security_level`}
                    placeholder={`${t('Select security level')}...`}
                    showSearch
                    optionFilterProp={'children'}
                    filterOption={filterOption}
                    value={
                      values.assets[formIndex].values.security_level ||
                      undefined
                    }
                    style={{ width: '100%' }}
                    onChange={(newSecurityLevel: string) => {
                      const isNoAuth =
                        newSecurityLevel === 'no_auth_or_privacy';
                      const isAuthNoPrivacy =
                        newSecurityLevel === 'auth_without_privacy';
                      setFieldValue(`assets.${formIndex}.values`, {
                        ...values.assets[formIndex].values,
                        ...(isNoAuth
                          ? {
                              auth_protocol: emptyNetworkAsset.auth_protocol,
                              auth_password: emptyNetworkAsset.auth_password,
                            }
                          : {}),
                        ...(isNoAuth || isAuthNoPrivacy
                          ? {
                              privacy_protocol:
                                emptyNetworkAsset.privacy_protocol,
                              privacy_password:
                                emptyNetworkAsset.privacy_password,
                            }
                          : {}),
                        security_level: newSecurityLevel,
                      });
                    }}
                    onBlur={handleBlur}
                    data-ta="assetSecurityLevelInput"
                  >
                    {securityLevels.map((option: Option) => (
                      <Select.Option key={option.key} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />
            </Grid.Col>
          )}
        </Grid.Row>
      </Grid.Col>
      {/* Versions 1 & 2 - Show community input for  */}
      {isV1orV2 && (
        <Grid.Col width={compact ? 4 : 12}>
          <FormGroup
            label={t('Community')}
            actions={createActions('community')}
            required
            error={
              <ErrorMessage name={`assets.${formIndex}.values.community`} />
            }
            input={
              <Input
                disabled={disabled}
                type="text"
                name={`assets.${formIndex}.values.community`}
                placeholder={t('Community')}
                value={values.assets[formIndex].values.community}
                onChange={handleChange}
                onBlur={handleBlur}
                data-ta="connectionCommunityInput"
              />
            }
          />
        </Grid.Col>
      )}
      {isV3NoAuthNoPrivacy && (
        <Grid.Col width={compact ? 4 : authColWidth}>
          <Grid.Row>
            <Grid.Col>
              <FormGroup
                label={t('Username')}
                actions={createActions('security_username')}
                required
                error={
                  <ErrorMessage
                    name={`assets.${formIndex}.values.security_username`}
                  />
                }
                input={
                  <Input
                    disabled={disabled}
                    type="text"
                    name={`assets.${formIndex}.values.security_username`}
                    placeholder={t('Security username')}
                    value={values.assets[formIndex].values.security_username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    data-ta="connectionSecurityUsernameInput"
                  />
                }
              />
            </Grid.Col>
          </Grid.Row>
        </Grid.Col>
      )}
      {isV3WithAuth && (
        <Grid.Col width={compact ? 4 : authColWidth}>
          <Grid.Row>
            <Grid.Col>
              <FormGroup
                label={t('Auth Protocol')}
                actions={createActions('auth_protocol')}
                required={true}
                error={
                  <ErrorMessage
                    name={`assets.${formIndex}.values.auth_protocol`}
                  />
                }
                input={
                  <Select
                    disabled={disabled}
                    name={`assets.${formIndex}.values.auth_protocol`}
                    placeholder={`${t('Select auth protocol')}...`}
                    showSearch
                    optionFilterProp={'children'}
                    filterOption={filterOption}
                    value={values.assets[formIndex].values.auth_protocol}
                    style={{ width: '100%' }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    data-ta="assetAuthProtocolInput"
                  >
                    {authProtocols.map((option: Option) => (
                      <Select.Option key={option.key} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col>
              <FormGroup
                label={t('Username')}
                actions={createActions('security_username')}
                required={true}
                error={
                  <ErrorMessage
                    name={`assets.${formIndex}.values.security_username`}
                  />
                }
                input={
                  <Input
                    disabled={disabled}
                    type="text"
                    name={`assets.${formIndex}.values.security_username`}
                    placeholder={t('Security username')}
                    value={values.assets[formIndex].values.security_username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    data-ta="connectionSecurityUsernameInput"
                  />
                }
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col>
              <FormGroup
                label={t('Password')}
                actions={createActions('auth_password')}
                required={true}
                error={
                  <ErrorMessage
                    name={`assets.${formIndex}.values.auth_password`}
                  />
                }
                input={
                  <Input.Password
                    disabled={disabled}
                    // type="password"
                    name={`assets.${formIndex}.values.auth_password`}
                    placeholder={t('Auth password')}
                    value={values.assets[formIndex].values.auth_password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    data-ta="connectionAuthPasswordInput"
                  />
                }
              />
            </Grid.Col>
          </Grid.Row>
        </Grid.Col>
      )}

      {isV3WithPrivacy && (
        <Grid.Col width={compact ? 4 : 6}>
          <Grid.Row>
            <Grid.Col>
              <FormGroup
                label={t('Privacy Protocol')}
                actions={createActions('privacy_protocol')}
                required={true}
                error={
                  <ErrorMessage
                    name={`assets.${formIndex}.values.privacy_protocol`}
                  />
                }
                input={
                  <Select
                    disabled={disabled}
                    name={`assets.${formIndex}.values.privacy_protocol`}
                    placeholder={`${t('Select privacy protocol')}...`}
                    showSearch
                    optionFilterProp={'children'}
                    filterOption={filterOption}
                    value={values.assets[formIndex].values.privacy_protocol}
                    style={{ width: '100%' }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    data-ta="assetPrivacyProtocolInput"
                  >
                    {privacyProtocols.map((option: Option) => (
                      <Select.Option key={option.key} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                }
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col>
              <FormGroup
                label={t('Privacy Password')}
                required={true}
                actions={createActions('privacy_password')}
                error={
                  <ErrorMessage
                    name={`assets.${formIndex}.values.privacy_password`}
                  />
                }
                input={
                  <Input.Password
                    disabled={disabled}
                    name={`assets.${formIndex}.values.privacy_password`}
                    placeholder={t('Privacy password')}
                    value={values.assets[formIndex].values.privacy_password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    data-ta="connectionPrivacyPasswordInput"
                  />
                }
              />
            </Grid.Col>
          </Grid.Row>
        </Grid.Col>
      )}

      {compact && <Grid.Col width={12} />}
      {testConnection && (
        <Grid.Col>
          <Flex h="end" v="center">
            {saveCredentials && <>{saveCredentials}</>}
            <Button
              variant="primary"
              disabled={
                disabled ||
                !version ||
                v3AndNoSecLevel ||
                noCommunity ||
                noPrivacy ||
                noUserPass ||
                noUser
              }
              onClick={testConnection}
              data-ta="testCredentialsButton"
            >
              {sameConnection
                ? t('Test existing credentials')
                : t('Test connection')}
            </Button>
          </Flex>
        </Grid.Col>
      )}
    </Grid.Row>
  );
};
